import moment from 'moment';
import { groupBy, pick } from 'lodash';
import { ServiceArea, TeamServiceArea } from '@bringg/types';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { registerAction } from '@bringg-frontend/global-stores';

export type ServiceAreaName = Pick<ServiceArea, 'id' | 'name' | 'external_id'>;

const NAME_FIELDS: (keyof ServiceAreaName)[] = ['id', 'name', 'external_id'];

const groupByTeam = (links: TeamServiceArea[]): Map<number, TeamServiceArea[]> =>
	new Map(Object.entries(groupBy(links, 'team_id')).map(([teamId, teamLinks]) => [Number(teamId), teamLinks]));

/** A link with no end date, or an end date after today. */
export const isTeamLinkActive = (link: TeamServiceArea, today: moment.Moment): boolean =>
	!link.effective_end_time || moment(link.effective_end_time).isAfter(today, 'day');

// The Angular app reads this store through the cross-app transport: it cannot import React stores.
export const GET_SERVICE_AREA_NAMES_STORE = 'get_service_area_names_store';

/**
 * Service area names for pages that show only names, without polygons.
 * Kept apart from the v2 SDK store on purpose: Routes Planner reads that store and drops areas with no polygon.
 */
class ServiceAreaNamesStore {
	private names: Map<number, ServiceAreaName> = new Map();
	private teamLinks: Map<number, TeamServiceArea[]> = new Map();

	private readonly _unregisterCrossAppAction: () => void;

	private _loadPromise: Promise<void> | null = null;
	private _resetCount = 0;

	constructor() {
		this._unregisterCrossAppAction = registerAction(
			GET_SERVICE_AREA_NAMES_STORE,
			() => this,
			getRootEnv().dashboardSdk.sdk.crossAppTransport
		);
	}

	/**
	 * Loads once until reset(). Concurrent callers share one request.
	 * A failed load is not kept, so the next call sends a new request.
	 * Callers of a load that reset() made out of date get the load after it.
	 */
	load(): Promise<void> {
		this._loadPromise ??= this.loadAndStore(this._resetCount);

		return this._loadPromise;
	}

	/** Called after the admin page saves service areas: the next load() fetches again. */
	reset(): void {
		this._resetCount++;
		this._loadPromise = null;
	}

	/** Ids not loaded (yet) are left out. */
	getByIds(ids: number[]): ServiceAreaName[] {
		return ids.map(id => this.names.get(id)).filter(Boolean);
	}

	/** Areas the team still serves (isTeamLinkActive). */
	getByTeam(teamId: number): ServiceAreaName[] {
		const today = moment();

		return (this.teamLinks.get(teamId) ?? [])
			.filter(link => isTeamLinkActive(link, today))
			.map(link => this.names.get(link.service_area_id))
			.filter(Boolean);
	}

	private async loadAndStore(resetCount: number): Promise<void> {
		try {
			const loaded = await this.fetch();

			if (resetCount !== this._resetCount) {
				return this.load();
			}

			this.names = loaded.names;
			this.teamLinks = loaded.teamLinks;
		} catch (error) {
			if (resetCount !== this._resetCount) {
				return this.load();
			}

			this._loadPromise = null;
			throw error;
		}
	}

	// Separate from loadAndStore so a synchronous SDK throw still reaches its catch after load() stored the promise.
	private async fetch(): Promise<{
		names: Map<number, ServiceAreaName>;
		teamLinks: Map<number, TeamServiceArea[]>;
	}> {
		const sdk = getRootEnv().dashboardSdk.sdk;
		const [names, { teams_service_areas }] = await Promise.all([
			sdk.v2.serviceAreas.loadAllByFields(['name', 'external_id']),
			sdk.teamServiceAreas.getAll()
		]);

		return {
			names: new Map(names.map(area => [area.id, pick(area, NAME_FIELDS)])),
			teamLinks: groupByTeam(teams_service_areas)
		};
	}

	resetAfterRemoveRoot(): void {
		this._unregisterCrossAppAction();
	}
}

export default ServiceAreaNamesStore;
